import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { toastr } from "react-redux-toastr";
import { API_BASE } from "APP_ROOT/constants";
import api from "APP_ROOT/api";
import { renderIf } from "MODULES/shared/decorators";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import { Button, Search } from "MODULES/shared/components";
import { OFFER, STANDARD } from "MODULES/IncomingQuotes/constants";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import { stringify } from "query-string";
import { matchesString } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import { ADMIN, EXPORT_LIMIT } from "MODULES/users/constants";
import { hasRole } from "MODULES/users/helpers";
import Table from "./Table";
import { incomingQuotePagination } from "../pagination";

const defaultFilters = {
  awaiting_reply: undefined,
  awaiting_reply_72: undefined,
  current_user: undefined,
  end_date: undefined,
  quote_type: undefined,
  start_date: undefined
};

export const INCOMING_QUOTE_FILTERS = [
  {
    label: "My Incoming Quotes",
    filters: { current_user: true }
  },
  {
    label: "All Incoming Quotes",
    filters: defaultFilters
  },
  {
    label: "Standard",
    filters: {
      quote_type: STANDARD
    }
  },
  {
    label: "Awaiting Reply (All)",
    filters: {
      quote_type: undefined,
      awaiting_reply: true,
      awaiting_reply_72: undefined
    }
  },
  {
    label: "Awaiting Reply (72 hours old)",
    filters: {
      quote_type: undefined,
      awaiting_reply_72: true,
      awaiting_reply: undefined
    },
    exclude_parts_view: true
  },
  {
    label: "All Offers Made",
    filters: { ...defaultFilters, quote_type: OFFER }
  }
];

export const INCOMING_QUOTE_FILTER_TAGS = [
  {
    label: "Show My IQs",
    key: "current_user"
  },
  {
    label: "Awaiting Reply (All)",
    key: "awaiting_reply"
  },
  {
    label: "Awaiting Reply (72 hours old)",
    key: "awaiting_reply_72"
  },
  {
    label: "All Offers Made",
    key: "quote_type",
    matchesTarget: matchesString(OFFER)
  },
  {
    label: "Standard Quotes",
    key: "quote_type",
    matchesTarget: matchesString(STANDARD)
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const incomingQuotesTotalCount = searchProps.paginator.get("totalCount");

  params.total_count = incomingQuotesTotalCount;
  return (
    <Button
      onClick={() => {
        if (incomingQuotesTotalCount > EXPORT_LIMIT) {
          toastr.info("Processing");
          api.incomingQuotes.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/incoming_quotes/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Incoming Quotes Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

const meetsExportCriteria = renderIf(({ searchProps }) => {
  const user = useCurrentUser();
  return searchProps.paginator.get("totalCount") <= 450 || hasRole(user, ADMIN);
});

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function IncomingQuoteSearch({ pushHistory }) {
  const [buyersFilter, setBuyersFilter] = useState([]);
  const [buyersTags, setBuyersTags] = useState([]);

  useEffect(() => {
    api.users
      .search({
        all_buyers: true,
        page: 1,
        sort: "first_name",
        reverse: false,
        limit: 1000
      })
      .then(resp => {
        const buyers = resp.data.results;

        // Build filter options for the buyer dropdown
        const buyersOptions = buyers.map(b => ({
          label: b.full_name,
          filters: { buyer_id: b.id }
        }));

        // Build tags for the TitleWithFilters
        // Note: We now use key: "buyer_id" to match the actual filter key.
        const tags = buyers.map(b => ({
          label: b.full_name,
          key: "buyer_id",
          matchesTarget: value => parseInt(value, 10) === b.id
        }));

        setBuyersFilter([
          {
            label: "View All",
            filters: { buyer_id: undefined }
          },
          ...buyersOptions
        ]);

        setBuyersTags(tags);
      })
      .catch(() => {
        toastr.error("Error", "Failed to fetch buyers.");
      });
  }, []);

  return (
    <Search search={incomingQuotePagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a vendor name, incoming quote number, part number, buyer, or date created..."
                submitOnSingleResult={r =>
                  pushHistory(`/incoming-quotes/${r.incoming_quote_id}`)}
              />
              <DecoratedExportButton searchProps={searchProps} />
              <DateRangePicker {...searchProps} />
              <FilterSelect
                {...searchProps}
                title="Buyer"
                filters={buyersFilter}
                right
              />
              <FilterSelect
                {...searchProps}
                title="Incoming Quotes"
                filters={INCOMING_QUOTE_FILTERS}
                right
              />
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Incoming Quotes"
            filters={[...INCOMING_QUOTE_FILTER_TAGS, ...buyersTags]} // Combine filters
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

IncomingQuoteSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, { pushHistory: push })(IncomingQuoteSearch);



// WEBPACK FOOTER //
// ./app/modules/IncomingQuotes/Search/IncomingQuotes.jsx